import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { QuestionRendererProps } from './types';
import { BarModel } from '../../../visuals/components/BarModel';
import { FALLBACK_COLOR, hexToRgba } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
/**
 * BarModelPartRenderer — Interactive strookmodel question.
 *
 * The student sees a bar model where a known whole is split into equal
 * segments. One or more segments are highlighted. The student types the
 * value of the highlighted region.
 *
 * Sprint R1 fixes:
 * - F1: honour moduleColor for input panel + accent (replaces hardcoded
 *   indigo)
 * - Surfaces missing config explicitly instead of using the
 *   `correctAnswer * 4` heuristic fallback (F8/F9 partial; full fix awaits
 *   F3 schema-validator gate at load-time).
 */
export const BarModelPartRenderer: React.FC<QuestionRendererProps> = ({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}) => {
  const color = moduleColor || FALLBACK_COLOR;
  const config = question.data?.barModelPart;
  const hasConfig = !!config && typeof config.total === 'number' && typeof config.segments === 'number' && Array.isArray(config.highlighted);
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Reset on question change
  useEffect(() => {
    setInputValue('');
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [question.id]);
  // Track ready state
  const isReady = inputValue.trim().length > 0;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  // Edge-triggered submit (R-03): normalize "1,5" → "1.5", fallback to '0' if empty.
  const answerPayload = useMemo(() => {
    const normalized = inputValue.replace(',', '.').trim();
    return normalized.length > 0 ? normalized : '0';
  }, [inputValue]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  if (!hasConfig) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-11305" data-mp="2bth7vnlwt5b5">
        <p className="text-amber-700 font-bold" data-id="element-11306" data-mp="2pt8przfjr5uf">
          ⚠️ Configuratie ontbreekt (barModelPart)
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-11307" data-mp="2losjhx20tweo">
          Deze vraag mist <code data-id="element-11308" data-mp="2zotljfu0v9vq">data.barModelPart</code> met
          <code data-id="element-11309" data-mp="2znscfgwkgxgn"> total</code>, <code data-id="element-11310" data-mp="2uqs6d5zpvupj">segments</code> en <code data-id="element-11311" data-mp="2urjy25q718aj">highlighted</code>
          .
        </p>
      </div>;
  }
  const total = config!.total as number;
  const segments = config!.segments as number;
  const highlighted = config!.highlighted as number[];
  const unit = config!.unit;
  const promptLabel = config!.promptLabel ?? 'Hoeveel is het gemarkeerde deel?';
  return <div className="w-full max-w-2xl mx-auto space-y-6" data-id="element-11312" data-mp="3igmeja93cv3g">
      {/* Strookmodel — the visual */}
      <div className="bg-white rounded-2xl p-6 sm:p-8 border-2 border-slate-200 shadow-sm" data-id="element-11313" data-mp="3diukoaovyywm">
        <BarModel total={total} totalLabel={config!.totalLabel} unit={unit} segments={segments} highlighted={highlighted} showUnknownMarker={true} size="md" data-id="element-11314" data-mp="y73dym630nm" />
      </div>

      {/* Input panel */}
      <div className="rounded-2xl p-5 sm:p-6 border-2" style={{
      backgroundColor: hexToRgba(color, 0.07),
      borderColor: hexToRgba(color, 0.25)
    }} data-id="element-11315" data-mp="3s37301l7f5r0">
        <label htmlFor="bar-model-input" className="block text-sm font-bold mb-3 uppercase tracking-wide" style={{
        color
      }} data-id="element-11316" data-mp="3j3k17eokbxxf">
          {promptLabel}
        </label>

        <div className="flex items-center gap-3" data-id="element-11317" data-mp="39e0h65p45y0z">
          {unit && <span className="text-2xl font-black tabular-nums" style={{
          color: hexToRgba(color, 0.55)
        }} data-id="element-11318" data-mp="2ver3pdsali0p">
              {unit}
            </span>}
          <div className="flex-1 relative" data-id="element-11319" data-mp="2sbbj3cp2ui6d">
            <input id="bar-model-input" ref={inputRef} type="text" inputMode="decimal" value={inputValue} onChange={e => {
            // Allow only digits, comma, dot, minus
            const cleaned = e.target.value.replace(/[^0-9.,\-]/g, '');
            setInputValue(cleaned);
          }} placeholder="?" className="w-full px-4 py-3 text-2xl font-black text-slate-800 bg-white border-2 rounded-xl focus:outline-none tabular-nums transition-all placeholder:text-slate-300 placeholder:font-normal" style={{
            borderColor: hexToRgba(color, 0.25),
            ['--tw-ring-color' as string]: hexToRgba(color, 0.18)
          } as React.CSSProperties} onFocus={e => {
            e.currentTarget.style.borderColor = color;
            e.currentTarget.style.boxShadow = `0 0 0 4px ${hexToRgba(color, 0.15)}`;
          }} onBlur={e => {
            e.currentTarget.style.borderColor = hexToRgba(color, 0.25);
            e.currentTarget.style.boxShadow = 'none';
          }} aria-label={promptLabel} autoComplete="off" data-id="element-11320" data-mp="2nda2cyf4iuy2" />
            <AnimatePresence data-id="element-11321" data-mp="su026aokeriv">
              {isReady && <motion.div initial={{
              scale: 0,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0,
              opacity: 0
            }} className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-500" aria-hidden data-id="element-11322" data-mp="svkwbz6yw8g7">
                  <ArrowRight className="w-6 h-6" data-id="element-11323" data-mp="2i9zhyamovf7h" />
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        <p className="text-xs mt-3 font-medium" style={{
        color: hexToRgba(color, 0.7)
      }} data-id="element-11324" data-mp="e3a3mq5sgdbf">
          Tip: het hele deel is{' '}
          {typeof total === 'number' ? total.toLocaleString('nl-NL') : total}
          {unit ? ` ${unit}` : ''}, verdeeld in {segments} gelijke stukken.
        </p>
      </div>
    </div>;
};